import {
  SmartTableColumn,
  Tooltip,
  TooltipTrigger,
} from '@toggle/design-system';
import { utcFormat } from 'd3';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorMessageWrapper } from '~/components/error-message-wrapper/ErrorMessageWrapper';
import { useDocumentsEntity } from '~/hooks/use-documents-entity/useDocumentsEntity';
import { DocumentAssistant } from '~/modules/document-assistant/DocumentAssistant';

import * as S from './DocumentWidget.styles';
import { DocumentTableItem, groupDocumentByYear, isFirstItem } from './utils';

export interface DocumentProps {
  entityTag: string;
}

export const Document = ({ entityTag }: DocumentProps) => {
  const [activeDocumentName, setActiveDocumentName] = useState('');
  const { t } = useTranslation([
    'widget',
    'asset-overview',
    'chat',
    'common',
    'intel',
  ]);

  const {
    data: documents,
    isError,
    refetch,
    isLoading,
    isRefetching,
  } = useDocumentsEntity(entityTag);

  const onOpenDrawer = (documentName: string) => {
    setActiveDocumentName(documentName);
  };

  const onCloseDrawer = () => {
    setActiveDocumentName('');
  };

  const docsGroupedByYear = groupDocumentByYear(documents);
  const columns: SmartTableColumn<DocumentTableItem>[] = [
    {
      label: t('widget:assetOverview.tableDocuments.year'),
      key: 'year',
      renderCell: props => <S.Cell $withBorder={false} {...props} />,
      render: (value, item) =>
        isFirstItem(docsGroupedByYear, item) ? (
          <S.NumericalValue>{value}</S.NumericalValue>
        ) : (
          <span />
        ),
    },
    {
      label: t('widget:assetOverview.tableDocuments.quarter'),
      key: 'period',
      render: value => <S.NumericalValue>{value}</S.NumericalValue>,
    },
    {
      label: t('widget:assetOverview.tableDocuments.filingDate'),
      key: 'release_date',
      render: (_, document) => {
        const date = new Date(document.release_date);
        const formatTime = utcFormat('%Y-%m-%d')(date);
        return <S.NumericalValue>{formatTime}</S.NumericalValue>;
      },
    },
    {
      label: t('widget:assetOverview.tableDocuments.Q&A'),
      key: 'alfred',
      alignment: 'center',
      render: (_, document) => {
        return (
          <Tooltip
            label={
              document.indexed
                ? t('widget:earnings:documentAssistant.askQuestions')
                : t('widget:earnings:documentAssistant.beingAnalyzed')
            }
            isTouchDevice={false}
            trigger={TooltipTrigger.Hover}
          >
            <S.IconWrapper>
              <S.OpenDocIcon
                data-testid="alfred-icon"
                iconName="AlfredNav"
                size={24}
                $indexed={document.indexed}
                onClick={() => {
                  if (document.indexed) {
                    onOpenDrawer(document.name);
                  }
                }}
              />
            </S.IconWrapper>
          </Tooltip>
        );
      },
    },
    {
      label: t('widget:assetOverview.tableDocuments.DocumentType'),
      key: 'kind',
    },
    {
      label: t('widget:assetOverview.tableDocuments.reference'),
      key: 'reference',
    },
    {
      label: t('widget:assetOverview.tableDocuments.file'),
      key: 'name',
      render: (_, document) => (
        <S.PdfLink href={document.location} target="_blank">
          <S.PdfWrapper>
            <S.PdfIcon src="/app/static/images/icons/pdf.svg" />
            {document.name}
          </S.PdfWrapper>
        </S.PdfLink>
      ),
    },
  ];

  if (!documents?.length && (isLoading || isRefetching)) {
    return <S.StyledSkeletonLoader data-testid="document-widget-loader" />;
  }

  if (!documents?.length || isError) {
    return (
      <ErrorMessageWrapper
        displayIcon
        variant={isError ? 'error' : 'empty'}
        onClick={refetch}
      />
    );
  }

  return (
    <S.Root>
      <S.StyledSmartTable
        isHeaderSticky
        columns={columns}
        data={documents}
        renderActionColumn={() => <></>}
      />
      <DocumentAssistant
        defaultOption={activeDocumentName}
        options={documents.map(doc => ({
          label: doc.name,
          documentNames: [doc.name],
        }))}
        onClose={onCloseDrawer}
        headerIcon={
          <img
            src="/app/static/images/illustrations/document-search.jpg"
            alt=""
            width="80px"
            height="80px"
          />
        }
        subtitle={t('widget:documentAssistant:subtitle')}
        optionIcon={<S.PdfIcon src="/app/static/images/icons/pdf.svg" />}
        chatPlaceholder={t('widget:documentAssistant.chatPlaceholder')}
      />
    </S.Root>
  );
};
